import { useEffect, useState } from "react";
import { useClientAuth } from "../context/ClientAuthContext";
import { clientApi } from "../lib/clientApi";
import { useLang } from "../i18n/LanguageContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import AvailabilityGrid from "../components/AvailabilityGrid";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { LogOut, User, CalendarCheck, ArrowRight, Clock, Info } from "lucide-react";

function formatError(detail) {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const Field = ({ label, type = "text", value, onChange, required, testid }) => (
  <div>
    <label className="block text-sm font-semibold text-forest mb-1.5">
      {label} {required && <span className="text-burnt">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      className="input-line"
    />
  </div>
);

const LoginForm = () => {
  const { t } = useLang();
  const { login } = useClientAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("account.welcome"));
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="login-form">
      <Field label={t("account.email")} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required testid="login-email" />
      <Field label={t("account.password")} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required testid="login-password" />
      <button type="submit" disabled={loading} data-testid="login-submit" className="btn-primary w-full justify-center disabled:opacity-60">
        {loading ? "..." : t("account.login_submit")}
      </button>
    </form>
  );
};

const RegisterForm = () => {
  const { t, lang } = useLang();
  const { register } = useClientAuth();
  const [f, setF] = useState({ name: "", email: "", phone: "", address: "", city: "", postal_code: "", province: "", password: "", confirm: "" });
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return toast.error(t("account.password_mismatch"));
    setLoading(true);
    try {
      const { confirm, ...payload } = f;
      await register({ ...payload, availability, language: lang });
      toast.success(t("account.manager_notice"));
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="register-form">
      <Field label={t("account.name")} value={f.name} onChange={set("name")} required testid="register-name" />
      <Field label={t("account.email")} type="email" value={f.email} onChange={set("email")} required testid="register-email" />
      <Field label={t("account.phone")} value={f.phone} onChange={set("phone")} testid="register-phone" />
      <Field label={t("account.address")} value={f.address} onChange={set("address")} testid="register-address" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label={t("account.city")} value={f.city} onChange={set("city")} testid="register-city" />
        <Field label={t("account.postal_code")} value={f.postal_code} onChange={set("postal_code")} testid="register-postal" />
        <Field label={t("account.province")} value={f.province} onChange={set("province")} testid="register-province" />
      </div>
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <Clock size={16} className="text-burnt" />
          <label className="text-sm font-semibold text-forest">{t("account.availability_title")}</label>
        </div>
        <p className="text-xs text-muted-foreground mb-3">{t("account.availability_help")}</p>
        <AvailabilityGrid value={availability} onChange={setAvailability} t={t} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t("account.password")} type="password" value={f.password} onChange={set("password")} required testid="register-password" />
        <Field label={t("account.password_confirm")} type="password" value={f.confirm} onChange={set("confirm")} required testid="register-confirm" />
      </div>
      <button type="submit" disabled={loading} data-testid="register-submit" className="btn-primary w-full justify-center disabled:opacity-60">
        {loading ? "..." : t("account.register_submit")}
      </button>
    </form>
  );
};

const StatusBadge = ({ s }) => {
  const map = {
    paid: "bg-green-100 text-green-700", confirmed: "bg-green-100 text-green-700",
    unpaid: "bg-amber-100 text-amber-700", pending: "bg-amber-100 text-amber-700",
    cancelled: "bg-red-100 text-red-700",
  };
  return <span className={`px-2 py-0.5 text-xs font-bold ${map[s] || "bg-gray-100 text-gray-600"}`}>{s}</span>;
};

const Dashboard = () => {
  const { t, lang } = useLang();
  const { user, logout, updateProfile } = useClientAuth();
  const [profile, setProfile] = useState({
    name: user.name || "", phone: user.phone || "", address: user.address || "",
    city: user.city || "", postal_code: user.postal_code || "", province: user.province || "",
  });
  const [availability, setAvailability] = useState(user.availability || {});
  const [bookings, setBookings] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setProfile((s) => ({ ...s, [k]: e.target.value }));

  useEffect(() => {
    clientApi.get("/auth/my-bookings").then((r) => setBookings(r.data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile({ ...profile, availability });
      toast.success(t("account.saved"));
    } catch {
      toast.error(t("account.password_mismatch"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="account-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
        <div>
          <p className="text-overline">{t("account.title")}</p>
          <h1 className="mt-3 hd-italic text-4xl md:text-5xl text-forest">
            {t("account.welcome")}, {user.name?.split(" ")[0] || ""}.
          </h1>
        </div>
        <button onClick={logout} data-testid="account-logout" className="btn-secondary self-start">
          <LogOut size={16} /> {t("account.logout")}
        </button>
      </div>

      <div data-testid="manager-notice" className="mb-10 flex items-start gap-3 bg-forest/5 border-l-4 border-burnt px-5 py-4">
        <Info size={20} className="text-burnt shrink-0 mt-0.5" />
        <p className="text-sm text-forest leading-relaxed">{t("account.manager_notice")}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Profile */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-2 mb-6">
            <User size={20} className="text-burnt" />
            <h2 className="hd-italic text-2xl text-forest">{t("account.profile_title")}</h2>
          </div>
          <form onSubmit={save} className="space-y-5 bg-bone border border-border p-8" data-testid="profile-form">
            <Field label={t("account.name")} value={profile.name} onChange={set("name")} testid="profile-name" />
            <div>
              <label className="block text-sm font-semibold text-forest mb-1.5">{t("account.email")}</label>
              <input value={user.email} disabled className="input-line opacity-60 cursor-not-allowed" data-testid="profile-email" />
            </div>
            <Field label={t("account.phone")} value={profile.phone} onChange={set("phone")} testid="profile-phone" />
            <Field label={t("account.address")} value={profile.address} onChange={set("address")} testid="profile-address" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label={t("account.city")} value={profile.city} onChange={set("city")} testid="profile-city" />
              <Field label={t("account.postal_code")} value={profile.postal_code} onChange={set("postal_code")} testid="profile-postal" />
              <Field label={t("account.province")} value={profile.province} onChange={set("province")} testid="profile-province" />
            </div>
            <button type="submit" disabled={saving} data-testid="profile-save" className="btn-primary disabled:opacity-60">
              {saving ? "..." : t("account.save")}
            </button>
          </form>

          <div className="mt-8">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={18} className="text-burnt" />
              <h3 className="hd-italic text-xl text-forest">{t("account.availability_title")}</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-3">{t("account.availability_help")}</p>
            <AvailabilityGrid value={availability} onChange={setAvailability} t={t} />
          </div>
        </div>

        {/* Bookings */}
        <div className="lg:col-span-7">
          <div className="flex items-center gap-2 mb-6">
            <CalendarCheck size={20} className="text-burnt" />
            <h2 className="hd-italic text-2xl text-forest">{t("account.bookings_title")}</h2>
          </div>
          {bookings.length === 0 ? (
            <div className="border border-border p-10 text-center" data-testid="no-bookings">
              <p className="text-muted-foreground">{t("account.no_bookings")}</p>
              <Link to="/reservation" className="btn-primary mt-6 inline-flex">
                {t("account.book_cta")} <ArrowRight size={16} />
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto border border-border">
              <table className="w-full text-sm">
                <thead className="bg-bone">
                  <tr>
                    <th className="text-left px-4 py-3 font-heading uppercase tracking-wide text-xs text-forest">{t("account.booking_date")}</th>
                    <th className="text-left px-4 py-3 font-heading uppercase tracking-wide text-xs text-forest">{t("account.booking_package")}</th>
                    <th className="text-left px-4 py-3 font-heading uppercase tracking-wide text-xs text-forest">{t("account.booking_status")}</th>
                    <th className="text-left px-4 py-3 font-heading uppercase tracking-wide text-xs text-forest">{t("account.booking_payment")}</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} data-testid={`account-booking-${b.id}`} className="border-t border-border">
                      <td className="px-4 py-3">{b.preferred_date}{b.preferred_time ? ` · ${b.preferred_time}` : ""}</td>
                      <td className="px-4 py-3">{lang === "fr" ? b.package_name_fr : b.package_name_en}</td>
                      <td className="px-4 py-3"><StatusBadge s={b.status} /></td>
                      <td className="px-4 py-3"><StatusBadge s={b.payment_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AccountPage = () => {
  const { t } = useLang();
  const { user, loading } = useClientAuth();

  return (
    <div data-testid="page-account" className="bg-white">
      <section className="container-flow section-pad">
        {loading ? (
          <p className="text-muted-foreground">{t("common.loading")}</p>
        ) : user ? (
          <Dashboard />
        ) : (
          <div className="max-w-lg mx-auto">
            <div className="text-center mb-10">
              <p className="text-overline">{t("account.title")}</p>
              <h1 className="mt-3 hd-italic text-4xl md:text-5xl text-forest">{t("account.title")}</h1>
            </div>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid grid-cols-2 mb-8 bg-bone border border-border p-1">
                <TabsTrigger value="login" data-testid="tab-login">{t("account.login_tab")}</TabsTrigger>
                <TabsTrigger value="register" data-testid="tab-register">{t("account.register_tab")}</TabsTrigger>
              </TabsList>
              <TabsContent value="login"><LoginForm /></TabsContent>
              <TabsContent value="register"><RegisterForm /></TabsContent>
            </Tabs>
          </div>
        )}
      </section>
    </div>
  );
};

export default AccountPage;
